import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateNL } from '../utils/dateFormat';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Globe, Send, Upload, X, Image as ImageIcon, MessageSquare, Camera, Download, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICE_TYPES = {
  aannemer: { nl: 'Aannemer', en: 'General Contractor' },
  timmerwerk: { nl: 'Timmerwerk', en: 'Carpentry' },
  stucwerk: { nl: 'Stucwerk', en: 'Plastering' },
  schilderwerk: { nl: 'Schilderwerk/Onderhouds Abonnement', en: 'Painting/Maintenance Subscription' },
  badkamer: { nl: 'Badkamer Specialist', en: 'Bathroom Specialist' },
  renovatie: { nl: 'Renovatie', en: 'Renovation' }
};

const STATUS_LABELS = {
  new: { nl: 'Nieuw', en: 'New' },
  scheduled: { nl: 'Gepland', en: 'Scheduled' },
  in_progress: { nl: 'Bezig', en: 'In Progress' },
  completed: { nl: 'Voltooid', en: 'Completed' },
  cancelled: { nl: 'Geannuleerd', en: 'Cancelled' }
};

export default function WorkRequestDetail() {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [files_bundle, setFilesBundle] = useState({ photos_company: [], photos_client: [], quotes: [], invoices: [] });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'nl' ? 'en' : 'nl';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      navigate('/client');
      return;
    }

    try {
      setLoading(true);
      const [requestRes, updatesRes, bundleRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/client/work-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/client/work-requests/${id}/updates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/client/me/work-requests/${id}/files`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { photos_company: [], photos_client: [], quotes: [], invoices: [] } })),
      ]);
      
      const foundRequest = requestRes.data.find(r => r.id === id);
      setRequest(foundRequest);
      setFilesBundle(bundleRes.data || { photos_company: [], photos_client: [], quotes: [], invoices: [] });
      setUpdates(updatesRes.data);
    } catch (error) {
      toast.error(i18n.language === 'nl' ? 'Fout bij laden' : 'Error loading');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClientUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const token = localStorage.getItem('clientToken');
    if (!token) return;
    setUploadingPhotos(true);
    try {
      // 1. Get signature
      const sigRes = await axios.get(`${BACKEND_URL}/api/client/me/cloudinary-signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sig = sigRes.data;
      const uploaded = [];
      for (const file of fileList) {
        // 2. Upload to Cloudinary
        const fd = new FormData();
        fd.append('file', file);
        fd.append('api_key', sig.api_key);
        fd.append('timestamp', sig.timestamp);
        fd.append('signature', sig.signature);
        fd.append('folder', sig.folder);
        const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: fd });
        if (!res.ok) {
          toast.error('Upload mislukt voor ' + file.name);
          continue;
        }
        const cloudResult = await res.json();
        uploaded.push({
          id: cloudResult.public_id,
          cloudinary_url: cloudResult.secure_url,
          cloudinary_public_id: cloudResult.public_id,
          cloudinary_thumbnail: `https://res.cloudinary.com/${sig.cloud_name}/image/upload/c_fill,g_auto,h_400,w_400,q_auto:good,f_auto/${cloudResult.public_id}`,
          filename: file.name,
        });
      }
      if (uploaded.length === 0) {
        toast.error('Geen foto kon worden geüpload');
        return;
      }
      // 3. Attach to backend (sets source='client')
      await axios.post(`${BACKEND_URL}/api/client/me/photos`, {
        photos: uploaded,
        address: request?.address || '',
        work_request_id: id,
        project: request?.title || '',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${uploaded.length} foto('s) toegevoegd`);
      // Re-fetch the bundle to display the new photos
      const bundleRes = await axios.get(`${BACKEND_URL}/api/client/me/work-requests/${id}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilesBundle(bundleRes.data);
    } catch (err) {
      toast.error('Upload mislukt');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSendUpdate = async () => {
    if (!message.trim() && files.length === 0) {
      toast.error(i18n.language === 'nl' ? 'Voeg een bericht of foto toe' : 'Add a message or photo');
      return;
    }

    const token = localStorage.getItem('clientToken');
    setSending(true);

    try {
      const formData = new FormData();
      formData.append('update_type', files.length > 0 ? 'photo' : 'message');
      if (message.trim()) {
        formData.append('message', message);
      }
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      await axios.post(`${BACKEND_URL}/api/client/work-requests/${id}/updates`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(i18n.language === 'nl' ? 'Verzonden!' : 'Sent!');
      setMessage('');
      setFiles([]);
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'nl' ? 'Fout bij verzenden' : 'Error sending');
    } finally {
      setSending(false);
    }
  };

  const handleDownloadReport = async () => {
    const token = localStorage.getItem('clientToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/work-requests/${id}/report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${request?.title || 'obra'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(i18n.language === 'nl' ? 'Rapport gedownload!' : 'Report downloaded!');
    } catch (error) {
      toast.error(i18n.language === 'nl' ? 'Fout bij downloaden' : 'Error downloading');
    }
  };

  const handleDownloadPhoto = async (photoId, filename) => {
    const token = localStorage.getItem('clientToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/download/photo/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(i18n.language === 'nl' ? 'Foto gedownload!' : 'Photo downloaded!');
    } catch (error) {
      toast.error(i18n.language === 'nl' ? 'Fout bij downloaden' : 'Error downloading');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center">
        <div className="text-white text-xl">{i18n.language === 'nl' ? 'Laden...' : 'Loading...'}</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center">
        <div className="text-white text-xl">{i18n.language === 'nl' ? 'Aanvraag niet gevonden' : 'Request not found'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/client/dashboard')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.language === 'nl' ? 'Terug' : 'Back'}
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadReport}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              {i18n.language === 'nl' ? 'Download Rapport' : 'Download Report'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-white hover:bg-white/10"
            >
              <Globe className="w-4 h-4 mr-2" />
              {i18n.language === 'nl' ? 'EN' : 'NL'}
            </Button>
          </div>
        </div>

        <Card className="bg-[#0d3d3d]/90 border-white/20 backdrop-blur mb-6">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white">{request.title}</CardTitle>
            <div className="flex gap-2 mt-2">
              <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                {SERVICE_TYPES[request.service_type]?.[i18n.language] || request.service_type}
              </span>
              <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                {STATUS_LABELS[request.status]?.[i18n.language] || request.status}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <strong className="text-white">{i18n.language === 'nl' ? 'Beschrijving' : 'Description'}:</strong>
              <p className="text-gray-300 mt-1">{request.description}</p>
            </div>
            <div>
              <strong className="text-white">{i18n.language === 'nl' ? 'Adres' : 'Address'}:</strong>
              <p className="text-gray-300 mt-1">{request.address}</p>
            </div>
            {request.preferred_start_date && (
              <div>
                <strong className="text-white">{i18n.language === 'nl' ? 'Gewenste startdatum' : 'Preferred start date'}:</strong>
                <p className="text-gray-300 mt-1">{formatDateNL(request.preferred_start_date)}</p>
              </div>
            )}
            {request.photos && request.photos.length > 0 && (
              <div>
                <strong className="text-white">{i18n.language === 'nl' ? "Foto's" : 'Photos'}:</strong>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {request.photos.map((photo) => (
                    <img
                      key={photo.id}
                      src={`data:${photo.file_type};base64,${photo.file_data}`}
                      alt={photo.filename}
                      className="w-full h-32 object-cover rounded"
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#0d3d3d]/90 border-white/20 backdrop-blur mb-6">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white">
              {i18n.language === 'nl' ? 'Updates' : 'Updates'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {updates.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                {i18n.language === 'nl' ? 'Nog geen updates' : 'No updates yet'}
              </p>
            ) : (
              <div className="space-y-4">
                {updates.map((update) => (
                  <div
                    key={update.id}
                    className={`p-4 rounded-lg ${
                      update.author_type === 'admin'
                        ? 'bg-blue-900/30 border-l-4 border-blue-500'
                        : 'bg-green-900/30 border-l-4 border-green-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {update.update_type === 'photo' ? <Camera className="w-4 h-4 text-white" /> : <MessageSquare className="w-4 h-4 text-white" />}
                        <strong className="text-white">{update.author_name}</strong>
                        <span className="text-xs text-gray-400">
                          {update.author_type === 'admin' ? 'Cardoso Bouw' : i18n.language === 'nl' ? 'U' : 'You'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(update.created_at).toLocaleString()}
                      </span>
                    </div>
                    {update.message && <p className="text-gray-200 mb-2">{update.message}</p>}
                    {update.photos && update.photos.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                        {update.photos.map((photo) => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={`data:${photo.file_type};base64,${photo.file_data}`}
                              alt={photo.filename}
                              className="w-full h-32 object-cover rounded"
                            />
                            <button
                              onClick={() => handleDownloadPhoto(photo.id, photo.filename)}
                              className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-700"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#0d3d3d]/90 border-white/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-white">
              {i18n.language === 'nl' ? 'Stuur een bericht of foto' : 'Send a message or photo'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">
                {i18n.language === 'nl' ? 'Bericht' : 'Message'}
              </Label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows="4"
                className="w-full bg-white/10 border-white/30 text-white rounded-md p-4"
                placeholder={i18n.language === 'nl' ? 'Typ uw bericht...' : 'Type your message...'}
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">
                {i18n.language === 'nl' ? "Foto's en Documenten" : 'Photos and Documents'}
              </Label>
              <label className="flex items-center justify-center w-full bg-white/10 border-2 border-dashed border-white/30 rounded-lg p-4 cursor-pointer hover:bg-white/20 transition">
                <div className="text-center">
                  <Upload className="w-6 h-6 text-white mx-auto mb-2" />
                  <p className="text-white text-sm">
                    {i18n.language === 'nl' ? "Klik om foto's of documenten te uploaden" : 'Click to upload photos or documents'}
                  </p>
                  <p className="text-white/60 text-xs mt-1">
                    {i18n.language === 'nl' ? 'Afbeeldingen, PDF, Word, Excel' : 'Images, PDF, Word, Excel'}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              
              {files.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {files.map((file, index) => {
                    const isImage = file.type.startsWith('image/');
                    const isPDF = file.type === 'application/pdf';
                    
                    return (
                      <div key={index} className="relative bg-white/10 rounded p-2">
                        {isImage ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-20 object-cover rounded"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-20 bg-blue-900/30 rounded">
                            <FileText className="w-8 h-8 text-blue-300 mb-1" />
                            <p className="text-xs text-white truncate w-full px-1 text-center">
                              {isPDF ? 'PDF' : file.name.split('.').pop().toUpperCase()}
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <p className="text-xs text-white/80 truncate mt-1">{file.name}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              onClick={handleSendUpdate}
              disabled={sending || (!message.trim() && files.length === 0)}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? '...' : (i18n.language === 'nl' ? 'Verzenden' : 'Send')}
            </Button>
          </CardContent>
        </Card>

        {/* Klusbestanden: foto's, offertes en facturen */}
        <Card className="bg-[#0d3d3d]/90 border-white/20 mt-6" data-testid="klus-files-section">
          <CardHeader>
            <CardTitle className="text-white text-xl">
              {i18n.language === 'nl' ? 'Mijn klusbestanden' : 'My job files'}
            </CardTitle>
            <p className="text-white/60 text-sm">
              {i18n.language === 'nl' ? `Adres: ${request.address}` : `Address: ${request.address}`}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Offertes */}
            {files_bundle.quotes.length > 0 && (
              <div data-testid="klus-quotes-block">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-[#3da5a5]" /> {i18n.language === 'nl' ? 'Offertes' : 'Quotes'} ({files_bundle.quotes.length})</h4>
                <div className="space-y-2">
                  {files_bundle.quotes.map(q => (
                    <div key={q.id} className="bg-white/5 border border-white/10 rounded p-3 flex items-center justify-between text-sm">
                      <div>
                        <p className="text-white font-medium">{q.number}</p>
                        <p className="text-white/60 text-xs">{q.offer_date} · {q.status}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-[#3da5a5] font-bold">€ {Number(q.total || 0).toLocaleString('nl-NL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                        {q.public_token && (
                          <a href={`/offerte/${q.public_token}`} target="_blank" rel="noreferrer" className="text-[#3da5a5] hover:underline text-xs" data-testid={`klus-open-quote-${q.id}`}>
                            {i18n.language === 'nl' ? 'Openen' : 'Open'} →
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Facturen */}
            {files_bundle.invoices.length > 0 && (
              <div data-testid="klus-invoices-block">
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-[#3da5a5]" /> {i18n.language === 'nl' ? 'Facturen' : 'Invoices'} ({files_bundle.invoices.length})</h4>
                <div className="space-y-2">
                  {files_bundle.invoices.map(inv => (
                    <div key={inv.id} className="bg-white/5 border border-white/10 rounded p-3 flex items-center justify-between text-sm">
                      <div>
                        <p className="text-white font-medium">{inv.number}{inv.type === 'credit' && ' (Credit)'}</p>
                        <p className="text-white/60 text-xs">{inv.invoice_date} · {inv.mollie_payment?.status === 'paid' || inv.status === 'betaald' ? '✓ Betaald' : 'Open'}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-[#3da5a5] font-bold">€ {Number(inv.total_incl || 0).toLocaleString('nl-NL', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                        {inv.public_token && (
                          <a href={`/factuur/${inv.public_token}`} target="_blank" rel="noreferrer" className="text-[#3da5a5] hover:underline text-xs" data-testid={`klus-open-invoice-${inv.id}`}>
                            {i18n.language === 'nl' ? 'Openen' : 'Open'} →
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cardoso Bouw foto's */}
            <div data-testid="klus-company-photos-block">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Camera className="w-4 h-4 text-[#3da5a5]" /> {i18n.language === 'nl' ? "Foto's van Cardoso Bouw" : 'Photos by Cardoso Bouw'} ({files_bundle.photos_company.length})</h4>
              {files_bundle.photos_company.length === 0 ? (
                <p className="text-white/40 text-sm">{i18n.language === 'nl' ? "Nog geen foto's geüpload door Cardoso Bouw" : 'No photos uploaded yet'}</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {files_bundle.photos_company.map((p, i) => {
                    const src = p.cloudinary_thumbnail || p.thumbnail || p.cloudinary_url || p.url;
                    const full = p.cloudinary_url || p.url;
                    return (
                      <a key={p.id || i} href={full} target="_blank" rel="noreferrer" className="aspect-square block rounded overflow-hidden bg-white/10" data-testid={`klus-company-photo-${i}`}>
                        <img src={src} alt={p.project || 'foto'} loading="lazy" className="w-full h-full object-cover" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mijn foto's */}
            <div data-testid="klus-client-photos-block">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-amber-300" /> {i18n.language === 'nl' ? "Mijn foto's" : 'My photos'} ({files_bundle.photos_client.length})</h4>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleClientUpload(e.target.files)}
                    disabled={uploadingPhotos}
                    data-testid="klus-upload-input"
                  />
                  <span className="inline-flex items-center gap-1 bg-[#3da5a5] hover:bg-[#2d8585] text-white px-3 py-1.5 rounded text-sm font-medium">
                    <Upload className="w-4 h-4" />
                    {uploadingPhotos ? '...' : (i18n.language === 'nl' ? 'Foto uploaden' : 'Upload photo')}
                  </span>
                </label>
              </div>
              {files_bundle.photos_client.length === 0 ? (
                <p className="text-white/40 text-sm">{i18n.language === 'nl' ? "U heeft nog geen foto's geüpload" : "You haven't uploaded any photos yet"}</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {files_bundle.photos_client.map((p, i) => {
                    const src = p.cloudinary_thumbnail || p.thumbnail || p.cloudinary_url || p.url;
                    const full = p.cloudinary_url || p.url;
                    return (
                      <a key={p.id || i} href={full} target="_blank" rel="noreferrer" className="aspect-square block rounded overflow-hidden bg-amber-50/20 border border-amber-300/30" data-testid={`klus-client-photo-${i}`}>
                        <img src={src} alt={p.address || 'mijn foto'} loading="lazy" className="w-full h-full object-cover" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
